import { Client } from '@stomp/stompjs';
import VueRouter from 'vue-router'
import utilities from '../assets/scripts/utilities';
import restService from './restService';
import store from '../stores/index';
import formLogData from '../services/formLogData';
import SockJS from 'sockjs-client';
import { ENGAGE_URLS, WEBSOCKET_HEADERS, WEBSOCKET_ERRORS, MESSAGE_ACTIONS, ROUTES, MUTATION_NAME, WEBSOCKET_END_POINTS, WEBSOCKET_STATUS, WEBSOCKET_MESSAGE, WEBSOCKET_PROPERTIES } from '../static/global-variables';

let stompClient = null;
let messageToken = null;
let socket = null;
let signing = false;
let disconnectCheck = false;
let isFirstRequest = true;
let connectTimeoutDelay = WEBSOCKET_PROPERTIES.TIMEOUT;
let connectCount = 1;
let queueTimeout;
let connectTimeout;

const clientId = utilities.generateUuid();
const connectHeaders = {
    "Veeva-Supported-Remote-Signatures": WEBSOCKET_HEADERS.SUPPORTED_REMOTE_SIGNATURES,
    "Veeva-Connection-Type": WEBSOCKET_HEADERS.CONNECTION_TYPE,
    "Veeva-Client-Id": clientId,
    "Veeva-App-Type": WEBSOCKET_HEADERS.APP_TYPE,
    "Veeva-App-Version": WEBSOCKET_HEADERS.APP_VERSION,
    "Veeva-Attendee-Id": null,
    "Authorization": null
};

const subscribeHeaders = {
    "Veeva-Connection-Type": WEBSOCKET_HEADERS.CONNECTION_TYPE,
    "Veeva-Attendee-Id": null,
    "Authorization": null,
    "Veeva-Client-Id": clientId
};

const navigationFallback = router => {
    return failure => {
        if (VueRouter.isNavigationFailure(failure, VueRouter.NavigationFailureType.cancelled)) {
            router.replace({ name: ROUTES.EXPIRED });
        }
    }
};

const messageNavigate = (router, name, params) => {
    const errorHandler = navigationFallback(router);
    router.push({
        name: name,
        params: params
    }).catch(errorHandler);
};

const addSubscription = (endPoint, messageHandler) => {
    return new Promise((resolve, reject) => {
        if(stompClient) {
            stompClient.subscribe(endPoint, messageHandler, subscribeHeaders);
            resolve();
        } else {
            reject();
        }
    })
};

const addAsyncSubscription = (endPoint, messageHandler) => {
    return new Promise((resolve, reject) => {
        if(stompClient) {
            stompClient.subscribe(endPoint, messageHandler(resolve, reject), subscribeHeaders);
        } else {
            reject();
        }
    })
}

const processRequestSignature = (message, router) => {
    if (isFirstRequest) {
        signing = true;
        store.commit(MUTATION_NAME.SET_REQUEST_FINISHED, false);
        if (message.signinType === MESSAGE_ACTIONS.MEDICAL_INQUIRY) {
            messageNavigate(router, ROUTES.SHARE_LINK_INQUIRIES, { signinId: message.signinId })
        } else if (message.signinType === MESSAGE_ACTIONS.SAMPLE) {
            messageNavigate(router, ROUTES.SHARE_LINK_SIGNEE, { signinId: message.signinId })
        } else if (message.signinType === MESSAGE_ACTIONS.MULTICHANNEL_CONSENT) {
            messageNavigate(router, ROUTES.SAMPLE_OPT_IN, { signinId: message.signinId })
        } else if (message.signinType === MESSAGE_ACTIONS.CONTENT_ACKNOWLEDGEMENT) {
            messageNavigate(router, ROUTES.AOC, { signinId: message.signinId })
        } else {
            messageNavigate(router, ROUTES.SHARE_LINK_SIGNEE, { signinId: message.signinId })
        }
        isFirstRequest = false;
    }

}

const processTimeoutMessage = (router, message) => {
    if (message.clientId === clientId) {
        disconnectCheck = true;
    }
    if (disconnectCheck) {
        queueTimeout ? clearTimeout(queueTimeout) : null;
        queueTimeout = setTimeout(() => {
            if (message.clientId !== clientId) {
                restService.logEvent(formLogData.formSocketMessageReceipt(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], message.attendeeAction, WEBSOCKET_END_POINTS.ENGAGE_CHANNEL_END_POINT));
                messageNavigate(router, ROUTES.EXPIRED);
                deactivate();
            }
        }, WEBSOCKET_MESSAGE.ENGAGE_CHANNEL_DELAY);
    }
}

const engageMessageWrapper = router => {
    return message => {
        let parsedMessage;
        if (message) {
            parsedMessage = JSON.parse(message.body);
            if (parsedMessage.action === MESSAGE_ACTIONS.REQUEST_SIGNATURE) {
                processRequestSignature(parsedMessage, router);
            }
            if (parsedMessage.channelStatus === MESSAGE_ACTIONS.CLOSED) {
                restService.logEvent(formLogData.formSocketMessageReceipt(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], parsedMessage.channelStatus, WEBSOCKET_END_POINTS.ENGAGE_CHANNEL_END_POINT));
                if (!store.getters.requestFinished) {
                    router.push(signing ? { name: ROUTES.CANCELED } : { name: ROUTES.EXPIRED });
                    signing = false;
                    store.commit(MUTATION_NAME.SET_REQUEST_FINISHED, true);
                }
                deactivate();
            } else if (parsedMessage.channelStatus === MESSAGE_ACTIONS.EXPIRED) {
                router.push({ name: ROUTES.EXPIRED });
                signing = false;
                store.commit(MUTATION_NAME.SET_REQUEST_FINISHED, true);
            }
            else if (parsedMessage.attendeeAction === MESSAGE_ACTIONS.DISCONNECT) {
                processTimeoutMessage(router, parsedMessage);
            }
        }
        message.ack();
    }
};

const signatureMessageWrapper = router => {
    return resolve => {
        return message => {
            let parsedMessage;
            if (message) {
                // proceed with the next subscribe after any message receipt
                resolve();
                parsedMessage = JSON.parse(message.body);
            }
            if (parsedMessage.hostName) {
                store.commit(MUTATION_NAME.SET_HOST_NAME, parsedMessage.hostName);
            }
            restService.logEvent(formLogData.formSocketMessageReceipt(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], parsedMessage.action, WEBSOCKET_END_POINTS.SIGNATURE_END_POINT))
            if (parsedMessage.action === MESSAGE_ACTIONS.REQUEST_SIGNATURE) {
                processRequestSignature(parsedMessage, router);
            } else if (parsedMessage.action === MESSAGE_ACTIONS.CANCEL_SIGNATURE && signing) {
                router.push({
                    name: ROUTES.CANCELED
                })
                signing = false;
                store.commit(MUTATION_NAME.SET_REQUEST_FINISHED, true);
                deactivate();
            } else if (parsedMessage.action === MESSAGE_ACTIONS.FINISH_SIGNATURE) {
                if(signing) {
                    router.push({
                        name: ROUTES.COMPLETED
                    })
                    signing = false;
                    store.commit(MUTATION_NAME.SET_REQUEST_FINISHED, true);
                    clearTimeout(store.getters.expireTimeoutId);
                } else {
                    router.push({
                        name: ROUTES.EXPIRED
                    })
                }
                deactivate();
            }
            message.ack();
        }
    }
};

const activate = router => {
    return new Promise((resolve, reject) => {
        if (stompClient) {
            stompClient.onConnect = async () => {
                clearTimeout(connectTimeout);
                restService.logEvent(formLogData.formSocketConnectSuccess(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"]))
                await addAsyncSubscription(WEBSOCKET_END_POINTS.SIGNATURE_END_POINT, signatureMessageWrapper(router))
                    .catch(() => {
                        restService.logEvent(formLogData.formSubscribeError(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], WEBSOCKET_END_POINTS.SIGNATURE_END_POINT))
                    })
                restService.logEvent(formLogData.formSubscribeSuccess(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], WEBSOCKET_END_POINTS.SIGNATURE_END_POINT))
                await addSubscription(WEBSOCKET_END_POINTS.ENGAGE_CHANNEL_END_POINT, engageMessageWrapper(router))
                    .catch(() => {
                        restService.logEvent(formLogData.formSubscribeError(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], WEBSOCKET_END_POINTS.ENGAGE_CHANNEL_END_POINT))
                    })
                restService.logEvent(formLogData.formSubscribeSuccess(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], WEBSOCKET_END_POINTS.ENGAGE_CHANNEL_END_POINT))
                resolve();
            }
            stompClient.activate();
        } else {
            reject();
        }
    })
        .catch(() => {
            restService.logEvent(formLogData.formSocketConnectError(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"]));
        })
};

const deactivate = () => {
    if (stompClient) {
        restService.logEvent(formLogData.formSocketDeactivate(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"]));
        stompClient.deactivate();
    }
};

export default {
    isSocketActive: () => {
        return store.getters.shareLink && socket ? socket.readyState === WEBSOCKET_STATUS.ACTIVE : true
    },
    generateClient: (channelId, router) => {
        connectHeaders["Veeva-Attendee-Id"] = channelId;
        subscribeHeaders["Veeva-Attendee-Id"] = channelId;
        stompClient = new Client({
            connectHeaders: connectHeaders,
            debug: msg => console.debug(msg),
            reconnectDelay: WEBSOCKET_PROPERTIES.RECONNECT_DELAY,
            heartbeatIncoming: WEBSOCKET_PROPERTIES.HEARTBEAT,
            heartbeatOutgoing: WEBSOCKET_PROPERTIES.HEARTBEAT,
            discardWebsocketOnCommFailure: true,
            onStompError: (errorFrame) => {
                clearTimeout(connectTimeout);
                const errorMessage = errorFrame.headers.message;
                restService.logEvent(formLogData.formStompError(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"], errorMessage))
                if (errorMessage.includes(WEBSOCKET_ERRORS.MISSING_CHANNEL)) {
                    router.push({
                        name: ROUTES.LINK_ERROR
                    })
                } else if(errorMessage.includes(WEBSOCKET_ERRORS.RESOURCE_NOT_FOUND)) {
                    try {
                        const errorBody = JSON.parse(errorFrame.body);
                        store.commit(MUTATION_NAME.SET_HOST_NAME, errorBody.data ? errorBody.data.hostName : '');
                        store.commit(MUTATION_NAME.SET_BANNER_URL, errorBody.data ? errorBody.data.bannerUrl : '');
                    } finally {
                        router.push({
                            name: ROUTES.CANCELED
                        })
                    }
                } else {
                    router.push({
                        name: ROUTES.EXPIRED
                    })
                }
                deactivate();
            },
            onWebSocketClose: () => {
                if(connectHeaders["Veeva-Attendee-Id"] && connectHeaders["Veeva-Client-Id"]) {
                    restService.logEvent(formLogData.formSocketDisconnect(connectHeaders["Veeva-Attendee-Id"], connectHeaders["Veeva-Client-Id"]))
                }
            },
            beforeConnect: () => {
                return restService.getToken().then(tokenRes => {
                    clearTimeout(connectTimeout);
                    if (connectCount < 4) {
                        connectTimeoutDelay = connectTimeoutDelay * connectCount;
                        connectTimeout = setTimeout(() => {
                            stompClient.deactivate();
                            stompClient.activate();
                        }, connectTimeoutDelay);
                        connectCount ++;
                    } else {
                        router.push({
                            name: ROUTES.EXPIRED
                        })
                    }
                    messageToken = tokenRes.data.data.token;
                    connectHeaders.Authorization = `Bearer ${messageToken}`;
                    subscribeHeaders.Authorization = `Bearer ${messageToken}`;
                }).catch(error => console.error(error))
            },
            webSocketFactory: () => {
                socket = new SockJS(`${restService.getEngageServerHost(window.location.hostname)}${ENGAGE_URLS.API_V1}/hcp-message/ws-connect/`);
                return socket;
            }
        })
    },
    activate
}
